<template>
  <div
    ref="screenshotSelectionRef"
    v-if="show"
    id="screenshot"
    class="container"
    @mousemove="move"
    @mousedown="mouseDown"
    @mouseup="mouseUp"
  >
    <transition name="screenshot">
      <div class="Flash" v-if="tookScreenShot" />
    </transition>
    <div
      class="overlay"
      :class="{ highlighting: mouseIsDown }"
      :style="{ borderWidth: borderWidth }"
    />
    <div
      class="crosshairs"
      :class="{ hidden: isDragging }"
      :style="{ left: crossHairsLeft + 'px', top: crossHairsTop + 'px' }"
    />
    <div
      class="borderedBox"
      :class="{ hidden: !isDragging }"
      :style="{
        left: boxLeft + 'px',
        top: boxTop + 'px',
        width: boxEndWidth + 'px',
        height: boxEndHeight + 'px',
      }"
    />
  </div>
</template>

<script>
import html2canvas from 'html2canvas';

export default {
  name: 'ScreenshotSelection',
  data() {
    return {
      show: false,
      mouseIsDown: false,
      isDragging: false,
      tookScreenShot: false, // After the mouse is released

      // Used to calculate where to start showing the dragging area
      startX: 0,
      startY: 0,
      endX: 0,
      endY: 0,

      borderWidth: '',

      // Handling the positioning of the crosshairs
      crossHairsLeft: 0,
      crossHairsTop: 0,

      // The box that contains the border and all required numbers.
      boxTop: 0,
      boxLeft: 0,
      boxEndWidth: 0,
      boxEndHeight: 0,
      windowHeight: 0,
      windowWidth: 0,
    };
  },
  created() {
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        this.onCancelCaptureSelectionHandler();
      }
    };

    document.addEventListener('keydown', escapeHandler);

    this.$once('hook:destroyed', () => {
      document.removeEventListener('keydown', escapeHandler);
    });
  },
  mounted: function () {
    let self = this;

    this.windowWidth =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth;
    this.windowHeight =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight;

    // To recalculate the width and height if the screen size changes.
    window.onresize = function () {
      self.windowWidth =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth;
      self.windowHeight =
        window.innerHeight ||
        document.documentElement.clientHeight ||
        document.body.clientHeight;
    };
  },
  methods: {
    move(e) {
      this.crossHairsTop = e.clientY;
      this.crossHairsLeft = e.clientX;

      /*
       * Change how the borderWidth is being calculated based on the x and y values.
       * Calculate the box with the 1px border's positioning and width */
      if (this.mouseIsDown) {
        const endY = (this.endY = e.clientY),
          endX = (this.endX = e.clientX),
          startX = this.startX,
          startY = this.startY,
          windowWidth = this.windowWidth,
          windowHeight = this.windowHeight;

        // Calculating the values differently depending on how the user start's dragging.
        if (endX >= startX && endY >= startY) {
          this.isDragging = true;

          this.borderWidth =
            startY +
            'px ' +
            (windowWidth - endX) +
            'px ' +
            (windowHeight - endY) +
            'px ' +
            startX +
            'px';

          this.boxTop = startY;
          this.boxLeft = startX;
          this.boxEndWidth = endX - startX;
          this.boxEndHeight = endY - startY;
        } else if (endX <= startX && endY >= startY) {
          this.isDragging = true;
          this.borderWidth =
            startY +
            'px ' +
            (windowWidth - startX) +
            'px ' +
            (windowHeight - endY) +
            'px ' +
            endX +
            'px';

          this.boxLeft = endX;
          this.boxTop = startY;
          this.boxEndWidth = startX - endX;
          this.boxEndHeight = endY - startY;
        } else if (endX >= startX && endY <= startY) {
          this.isDragging = true;

          this.boxLeft = startX;
          this.boxTop = endY;
          this.boxEndWidth = endX - startX;
          this.boxEndHeight = startY - endY;
          this.borderWidth =
            endY +
            'px ' +
            (windowWidth - endX) +
            'px ' +
            (windowHeight - startY) +
            'px ' +
            startX +
            'px';
        } else if (endX <= startX && endY <= startY) {
          this.isDragging = true;

          this.boxLeft = endX;
          this.boxTop = endY;
          this.boxEndWidth = startX - endX;
          this.boxEndHeight = startY - endY;

          this.borderWidth =
            endY +
            'px ' +
            (windowWidth - startX) +
            'px ' +
            (windowHeight - startY) +
            'px ' +
            endX +
            'px';
        } else {
          this.isDragging = false;
        }
      }
    },
    mouseDown(e) {
      this.borderWidth = this.windowWidth + 'px ' + this.windowHeight + 'px';

      this.startX = e.clientX;
      this.startY = e.clientY;

      this.mouseIsDown = true;
      this.tookScreenShot = false;
    },
    async mouseUp(event) {
      this.borderWidth = 0;

      if (this.isDragging) {
        // Don't take the screenshot unless the mouse moved somehow.
        this.tookScreenShot = true;
        await this.takeCapture(
          this.boxEndWidth,
          this.boxEndHeight,
          Math.min(event.pageX, this.startX),
          Math.min(event.pageY, this.startY)
        );
      }

      this.isDragging = false;
      this.mouseIsDown = false;
    },
    async takeCapture(selectionWidth, selectionHeight, positionX, positionY) {
      const canvas = await html2canvas(document.querySelector('html'), {
        width: selectionWidth,
        height: selectionHeight,
        x: positionX,
        y: positionY,
      });

      canvas.style.display = 'none';
      document.body.appendChild(canvas);

      const image = canvas
        .toDataURL('image/png')
        .replace('image/png', 'image/octet-stream');

      this.$root.$emit('capture-selected', image);
    },
    onCancelCaptureSelectionHandler() {
      this.show = false;
      this.$root.$emit('capture-selected', null);
    },
  },
};
</script>

<style scoped>
/* Foundation */
*,
*:before,
*:after {
  box-sizing: border-box;
}

html,
body {
  padding: 0;
  margin: 0;
  height: 100%;
}

.overlay,
.crosshairs,
.tooltip,
.borderedBox {
  user-select: none;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}

.overlay.highlighting {
  background: none;
  border-color: rgba(0, 0, 0, 0.5);
  border-style: solid;
}

.crosshairs {
  height: 100%;
  position: absolute;
  width: 100%;
  z-index: 2147483645;
}

.crosshairs.hidden {
  display: none;
}

.crosshairs::before,
.crosshairs::after {
  content: '';
  height: 100%;
  width: 100%;
  position: absolute;
  border: none !important;
  border-image: !important;
}

.crosshairs::before {
  left: -100%;
  top: -100%;
  border-right: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.crosshairs::after {
  left: 0px;
  top: 0px;
  border-top: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-left: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.container {
  clear: both;
  overflow: hidden;
  width: 100%;
  height: 100%;
}

.borderedBox {
  border: 1px solid #fff;
  position: absolute;
}

.borderedBox.hidden {
  display: none;
}

.tooltip {
  display: inline-block;
  position: absolute;

  background-color: grey;
  color: #fff;

  border-radius: 4px;

  font-size: 12px;
  font-family: monospace;

  padding: 6px;
  margin: 6px;
  white-space: nowrap;
}

.tooltip.hidden {
  display: none;
}

.Flash {
  position: absolute;
  width: 100%;
  height: 100%;

  top: 0;
  left: 0;

  background-color: #fff;
  z-index: 2147483646;

  opacity: 1;

  animation-delay: 0.2s;
  animation-name: fade-out;
  animation-duration: 1s;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
}

.screenshot-enter-active,
.screenshot-leave-active {
  transition: opacity 0.2s;
}

.screenshot-enter, .screenshot-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

@keyframes fade-out {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}
</style>
